/**
 * History page
 */

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CircleAlertIcon, FileTextIcon, FilterIcon, SearchIcon } from "lucide-react";
import type { AuditListItemExtended } from "@/types";
import { Alert, AlertAction, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { notifyError, notifySuccess } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { HistoryFilters } from "@/features/history/HistoryFilters";
import { HistoryTable } from "@/features/history/HistoryTable";
import { useAuditHistory } from "@/features/history/useAuditHistory";
import { useAuditActions } from "@/features/history/useAuditActions";
import { Page } from "@/components/layout/Page";
import { PageHeader } from "@/components/layout/PageHeader";

type HistoryLocationState = {
  successMessage?: string;
};

export default function History() {
  const { t } = useTranslation(["history", "common"]);
  const navigate = useNavigate();
  const location = useLocation();
  const [showFilters, setShowFilters] = useState(false);

  const {
    audits,
    loading,
    error,
    page,
    total,
    limit,
    statusFilter,
    inputTypeFilter,
    dateFrom,
    dateTo,
    search,
    setSearch,
    sortBy,
    sortOrder,
    setPage,
    setStatusFilter,
    setInputTypeFilter,
    setDateFrom,
    setDateTo,
    setSortBy,
    setSortOrder,
    clearFilters,
    hasActiveFilters,
    fetchAudits,
  } = useAuditHistory({ t });

  const {
    deletingAuditId,
    deletingAll,
    canDeleteAll,
    handleDelete,
    handleDeleteAll,
  } = useAuditActions({
    total,
    hasActiveFilters,
    onSuccess: (message) => notifySuccess(message),
    onError: (message) => notifyError(message),
    t: (key, options) => t(`history:${key}`, options as Record<string, string>),
    onRefresh: fetchAudits,
    onPageReset: () => setPage(1),
  });

  // Surface the success message handed over from the Upload page as a Toast.
  // Guard against React StrictMode's double-invoke (and any re-render before
  // the state is cleared) firing the toast twice for the same navigation.
  const shownSuccessRef = useRef(false);
  useEffect(() => {
    const state = location.state as HistoryLocationState | null;
    if (
      shownSuccessRef.current ||
      typeof state?.successMessage !== "string" ||
      !state.successMessage
    ) {
      return;
    }
    shownSuccessRef.current = true;
    notifySuccess(state.successMessage);
    // Clear location state
    void navigate(location.pathname, { replace: true, state: {} });
  }, [location, navigate]);

  const handleAuditClick = (auditId: string, audit: AuditListItemExtended) => {
    if (audit.status === "COMPLETED") {
      void navigate(`/results/${auditId}`);
    }
  };

  const handleNewAudit = () => {
    void navigate("/");
  };

  // Page-load failures stay inline (persistent, actionable); transient
  // delete/delete-all failures are announced via Toast (see useAuditActions onError).
  const displayError = error;

  // The full-page spinner only appears on the FIRST load — i.e. when there's
  // literally nothing to show yet (no rows, no error, no totals). Once the
  // page has rendered once, every subsequent re-fetch (typing in the search
  // box, changing a filter, sort, page, etc.) must NOT swap the whole tree
  // out for a centered spinner: that would unmount the search input (losing
  // focus and selection mid-typing), remount HistoryTable, and produce the
  // visible "blink" users reported on /history. The inline indicator inside
  // HistoryTable handles the "refresh in flight" affordance for these cases.
  if (loading && audits.length === 0 && total === 0 && !error) {
    return (
      <div className="flex min-h-[60dvh] items-center justify-center">
        <div className="text-center">
          <Spinner className="mx-auto mb-4 size-12 text-[var(--primary)]" />
          <p className="text-[var(--muted-foreground)]">{t("history:loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <Page max={1140}>
      <PageHeader
        actions={
          <>
            <Button
              variant="outline"
              size="lg"
              onClick={() => setShowFilters(!showFilters)}
              data-test="history-filters-toggle"
              aria-expanded={showFilters}
              aria-controls="history-filters-content"
              className={cn(hasActiveFilters && "border-primary bg-accent")}
            >
              <FilterIcon aria-hidden="true" className="size-4" />
              {hasActiveFilters
                ? t("history:filtersButtonActive")
                : t("history:filtersButton")}
            </Button>
            <Button
              variant="destructive-outline"
              size="lg"
              onClick={handleDeleteAll}
              disabled={total === 0 || deletingAll || !canDeleteAll}
              data-test="history-delete-all"
            >
              {deletingAll
                ? t("history:deleteAll.deleting")
                : t("history:deleteAll.button")}
            </Button>
            <Button
              variant="cta"
              size="lg"
              onClick={handleNewAudit}
              data-test="history-new-audit"
            >
              {t("history:newAudit")}
            </Button>
          </>
        }
        sub={t("history:subtitle")}
        title={t("history:title")}
      />

      <div className="mb-4">
        <div className="relative">
          <Input
            aria-label={t("history:filters.search")}
            data-test="history-search"
            icon={<SearchIcon />}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("history:filters.searchPlaceholder")}
            type="search"
            value={search}
          />
        </div>
      </div>

      {showFilters && (
        <div className="mb-6" id="history-filters-content">
          <HistoryFilters
            filters={{
              status: statusFilter,
              inputType: inputTypeFilter,
              dateFrom,
              dateTo,
              hasActive: hasActiveFilters,
              onStatusChange: setStatusFilter,
              onInputTypeChange: setInputTypeFilter,
              onDateFromChange: setDateFrom,
              onDateToChange: setDateTo,
              onClear: clearFilters,
            }}
            sort={{
              by: sortBy,
              order: sortOrder,
              onByChange: setSortBy,
              onOrderToggle: () =>
                setSortOrder(sortOrder === "asc" ? "desc" : "asc"),
            }}
          />
        </div>
      )}

      {displayError && (
        <Alert className="mb-8" data-test="history-error" variant="error">
          <CircleAlertIcon aria-hidden="true" className="size-5" />
          <AlertDescription className="flex-1">
            <h2 className="mb-1 font-semibold text-[var(--foreground)]">
              {t("history:errorLoading.title")}
            </h2>
            <p>{displayError}</p>
          </AlertDescription>
          <AlertAction>
            <Button
              data-test="history-retry"
              onClick={() => void fetchAudits()}
              size="sm"
              variant="outline"
            >
              {t("common:buttons.retry")}
            </Button>
          </AlertAction>
        </Alert>
      )}

      {!displayError && total === 0 && (
        <div className="rounded-[var(--r-lg)] border border-[var(--border)] bg-[var(--card)] p-8 text-center">
          <FileTextIcon
            aria-hidden="true"
            className="mx-auto mb-4 size-16 text-[var(--muted-foreground)]"
          />
            <h2 className="mb-2 text-xl font-semibold text-[var(--foreground)]">
              {t("history:empty.title")}
            </h2>
            <p className="mb-6 text-[var(--muted-foreground)]">
              {t("history:empty.message")}
            </p>
            <Button
              variant="cta"
              size="xl"
              onClick={handleNewAudit}
              data-test="history-empty-new-audit"
            >
              {t("history:empty.button")}
            </Button>
          </div>
        )}

        {!displayError && total > 0 && (
          <HistoryTable
            audits={audits}
            page={page}
            total={total}
            limit={limit}
            loading={loading && audits.length > 0}
            deletingAuditId={deletingAuditId}
            onAuditClick={handleAuditClick}
            onDelete={handleDelete}
            onPageChange={setPage}
          />
        )}
    </Page>
  );
}
